import {
	DollarOutlined,
	InfoCircleOutlined,
	LeftOutlined,
	PercentageOutlined,
	PlusOutlined,
	RightOutlined,
} from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Button,
	Card,
	DatePicker,
	Input,
	InputNumber,
	Modal,
	message,
	Select,
	Table,
	Tag,
} from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import {
	useLocation,
	useNavigate,
	useOutletContext,
	useParams,
	useSearchParams,
} from "react-router-dom";
import type { AppRole } from "@/shared/config/navigation";
import {
	canManagePaymentSchedule,
	canUseEarlyPayment as canUseEarlyPaymentByRole,
} from "@/shared/config/permissions";
import { formatMoney } from "@/shared/utils/money";
import {
	getSaleById,
	submitEarlySchedulePayment,
	submitSchedulePayment,
} from "../services/sales.backend";
import type { ScheduleItem } from "../types/sales.types";

export function PaymentSchedulePage() {
	const navigate = useNavigate();
	const location = useLocation();
	const { saleId } = useParams();
	const [searchParams] = useSearchParams();
	const { role } = useOutletContext<{ role: AppRole }>();
	const queryClient = useQueryClient();

	const { data: sale, isLoading } = useQuery({
		queryKey: ["sale", saleId],
		queryFn: () => getSaleById(saleId!),
		enabled: !!saleId,
	});

	const canUseEarlyPayment = canUseEarlyPaymentByRole(role);
	const canChangeSchedule = canManagePaymentSchedule(role);

	// Modal states
	const [payModalVisible, setPayModalVisible] = useState(false);
	const [preselectedScheduleId, setPreselectedScheduleId] = useState<string | null>(null);
	const [paymentAmount, setPaymentAmount] = useState<number>(0);
	const [paymentMethod, setPaymentMethod] = useState("naqd");
	const [paymentReceipt, setPaymentReceipt] = useState("");
	const [paymentDate, setPaymentDate] = useState(dayjs().format("YYYY-MM-DD"));
	const [paymentNote, setPaymentNote] = useState("");

	const [earlyPayModalVisible, setEarlyPayModalVisible] = useState(false);
	const [earlyPayMonths, setEarlyPayMonths] = useState(6);
	const [earlyPayMethod, setEarlyPayMethod] = useState("bank");
	const [earlyPayReceipt, setEarlyPayReceipt] = useState("");

	const scheduleRows = sale?.schedule ?? [];

	// Action: single row payment modal trigger
	const handleOpenPayModal = (scheduleId: string, planned: number, paid: number) => {
		if (!canChangeSchedule) {
			message.warning("Faqat Org Admin to'lov grafigini o'zgartira oladi.");
			return;
		}

		setPreselectedScheduleId(scheduleId);
		const needed = Math.max(0, planned - paid);
		setPaymentAmount(Math.round(needed));
		setPaymentReceipt(`CH-${dayjs().format("YYYYMMDD")}-${Math.floor(Math.random() * 900 + 100)}`);
		setPaymentDate(dayjs().format("YYYY-MM-DD"));
		setPaymentNote("");
		setPayModalVisible(true);
	};

	const handlePaymentRowChange = (scheduleId: string) => {
		const row = scheduleRows.find((item) => item.id === scheduleId);
		setPreselectedScheduleId(scheduleId);
		if (row) {
			const needed = Math.max(0, row.plannedAmount - row.paidAmount);
			setPaymentAmount(Math.round(needed));
		}
	};

	const handleSinglePaymentSubmit = async () => {
		if (!canChangeSchedule) {
			message.warning("Faqat Org Admin to'lov grafigini o'zgartira oladi.");
			return;
		}

		if (!(sale && preselectedScheduleId !== null)) {
			return;
		}
		if (paymentAmount <= 0) {
			message.warning("Iltimos, to'lov summasini kiriting!");
			return;
		}

		await submitSchedulePayment({
			amount: paymentAmount,
			paymentMethod,
			receiptNumber: paymentReceipt,
			paymentDate,
			note: paymentNote || undefined,
			saleId: sale.id,
			scheduleId: preselectedScheduleId,
		});

		// Refresh sale data from backend
		await queryClient.invalidateQueries({ queryKey: ["sale", saleId] });

		message.success("To'lov muvaffaqiyatli kiritildi!");
		setPayModalVisible(false);
	};

	// Early payment discount calculations
	const discountInfo = useMemo(() => {
		let discountPercent = 0;
		if (earlyPayMonths >= 12) {
			discountPercent = 5;
		} else if (earlyPayMonths >= 6) {
			discountPercent = 3;
		} else if (earlyPayMonths >= 3) {
			discountPercent = 1;
		}

		const unpaidRows = scheduleRows.filter((item) => item.status !== "paid");
		const rowsToPay = unpaidRows.slice(0, earlyPayMonths);
		const rawSum = rowsToPay.reduce((sum, item) => sum + item.plannedAmount, 0);

		const discountAmount = Math.round(rawSum * (discountPercent / 100));
		const finalPayAmount = Math.round(rawSum - discountAmount);

		return {
			discountPercent,
			rawSum: Math.round(rawSum),
			discountAmount,
			finalPayAmount,
			rowsCount: rowsToPay.length,
		};
	}, [scheduleRows, earlyPayMonths]);

	if (isLoading) {
		return <div className="p-8 text-center text-gray-500">Yuklanmoqda...</div>;
	}

	if (!sale) {
		return (
			<div className="p-8 text-center text-gray-500">
				Sotuv topilmadi! <Button onClick={() => navigate("/sales")}>Ro'yxatga qaytish</Button>
			</div>
		);
	}

	const remainingAmount = Math.max(0, sale.finalPrice - sale.paidAmount);
	const returnClientId = searchParams.get("clientId");
	const returnToRoomId = (location.state as any)?.returnToRoomId;
	const returnToRoomLabel = (location.state as any)?.returnToRoomLabel;
	const backPath = returnToRoomId
		? `/rooms/${returnToRoomId}`
		: returnClientId
			? `/clients/${returnClientId}`
			: `/sales/${sale.id}`;
	const backLabel = returnToRoomId
		? returnToRoomLabel || `Xona #${sale.roomNumber}`
		: returnClientId
			? "Mijoz kartochkasi"
			: `Sotuv #${sale.saleNumber}`;

	const handleOpenEarlyPayModal = () => {
		if (!canUseEarlyPayment) {
			message.warning("Super Admin erta to'lov qabul qila olmaydi.");
			return;
		}

		setEarlyPayReceipt(
			`CH-EARLY-${dayjs().format("YYYYMMDD")}-${Math.floor(Math.random() * 90 + 10)}`
		);
		setEarlyPayModalVisible(true);
	};

	const handleEarlyPaymentSubmit = async () => {
		if (!canUseEarlyPayment) {
			message.warning("Super Admin erta to'lov qabul qila olmaydi.");
			return;
		}

		if (discountInfo.rowsCount === 0) {
			message.warning("To'lanadigan oylar topilmadi!");
			return;
		}

		await submitEarlySchedulePayment({
			monthsCount: earlyPayMonths,
			paymentMethod: earlyPayMethod,
			receiptNumber: earlyPayReceipt,
			paymentDate: dayjs().format("YYYY-MM-DD"),
			saleId: sale.id,
		});

		// Refresh sale data from backend
		await queryClient.invalidateQueries({ queryKey: ["sale", saleId] });

		message.success(
			`${discountInfo.rowsCount} oylik erta to'lov qabul qilindi, chegirma qo'llandi!`
		);
		setEarlyPayModalVisible(false);
	};

	const scheduleColumns = [
		{
			title: "#",
			dataIndex: "index",
			key: "index",
			width: 60,
			render: (val: number) => <span className="font-bold">{val}</span>,
		},
		{
			title: "Sana",
			dataIndex: "date",
			key: "date",
		},
		{
			title: "Reja Summa",
			dataIndex: "plannedAmount",
			key: "plannedAmount",
			render: (val: number) => `${formatMoney(val)} UZS`,
		},
		{
			title: "To'langan",
			dataIndex: "paidAmount",
			key: "paidAmount",
			render: (val: number) => `${formatMoney(val)} UZS`,
		},
		{
			title: "Holat",
			dataIndex: "status",
			key: "status",
			render: (status: string) => {
				let color = "default";
				let text = "Rejada";
				if (status === "paid") {
					color = "success";
					text = "To'landi";
				} else if (status === "expected") {
					color = "warning";
					text = "Kutilmoqda";
				} else if (status === "delayed") {
					color = "error";
					text = "Kechikkan";
				}
				return <Tag color={color}>{text}</Tag>;
			},
		},
		{
			title: "Kechikish",
			dataIndex: "delayDays",
			key: "delayDays",
			render: (val: number, record: ScheduleItem) => {
				if (record.status === "delayed" && val > 0) {
					return <span className="text-red-500 font-bold">{val} kun</span>;
				}
				if (record.status === "expected") {
					return <span className="text-amber-500 text-xs">Yaqinda</span>;
				}
				return <span className="text-gray-400">-</span>;
			},
		},
		{
			title: "Amallar",
			key: "actions",
			width: 150,
			render: (_: any, record: ScheduleItem) => {
				if (!canChangeSchedule) {
					return <span className="text-gray-400 text-xs">Faqat ko'rish</span>;
				}
				if (record.status === "paid") {
					return <span className="text-green-600 font-semibold text-xs">Yopilgan</span>;
				}
				return (
					<Button
						type="primary"
						ghost
						size="small"
						icon={<DollarOutlined />}
						onClick={() => {
							if (!record.id) {
								return;
							}
							handleOpenPayModal(record.id, record.plannedAmount, record.paidAmount);
						}}
					>
						To'lov
					</Button>
				);
			},
		},
	];

	return (
		<div className="crm-payment-schedule">
			<div className="crm-page-head">
				<div>
					<Button
						className="crm-back-link"
						icon={<LeftOutlined />}
						onClick={() => navigate(backPath, { state: location.state })}
					>
						{backLabel}
					</Button>
					<h1>To'lov grafigi</h1>
					<p style={{ color: "var(--crm-ink-3)" }}>
						Mijoz: {sale.clientName} - Xona: {sale.roomNumber} ({sale.buildingName})
					</p>
				</div>
				<div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
					<Button onClick={() => navigate(`/sales/${sale.id}`, { state: location.state })}>
						Sotuv <RightOutlined />
					</Button>
					{canChangeSchedule ? (
						<Button
							type="primary"
							className="crm-primary-btn"
							icon={<PlusOutlined />}
							onClick={() => {
								const firstUnpaid = sale.schedule.find((s) => s.status !== "paid");
								if (firstUnpaid?.id) {
									handleOpenPayModal(
										firstUnpaid.id,
										firstUnpaid.plannedAmount,
										firstUnpaid.paidAmount
									);
								} else {
									message.info("Grafik to'liq yopilgan!");
								}
							}}
						>
							To'lov kiritish
						</Button>
					) : null}
					{sale.paymentType === "muddatli" && canUseEarlyPayment && canChangeSchedule && (
						<Button
							style={{
								backgroundColor: "var(--crm-green)",
								color: "#fff",
								borderColor: "var(--crm-green)",
							}}
							icon={<PercentageOutlined />}
							onClick={handleOpenEarlyPayModal}
						>
							Erta to'lov (Skidka)
						</Button>
					)}
				</div>
			</div>

			{/* Financial KPIs */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
				<Card className="crm-card">
					<div className="text-gray-400 text-xs">Shartnoma summasi</div>
					<div className="text-xl font-extrabold text-blue-600 mt-1">
						{formatMoney(sale.finalPrice)} UZS
					</div>
				</Card>
				<Card className="crm-card">
					<div className="text-gray-400 text-xs">Jami to'landi</div>
					<div className="text-xl font-extrabold text-emerald-600 mt-1">
						{formatMoney(sale.paidAmount)} UZS
					</div>
				</Card>
				<Card className="crm-card">
					<div className="text-gray-400 text-xs">Qoldiq qarz summasi</div>
					<div className="text-xl font-extrabold text-indigo-600 mt-1">
						{formatMoney(remainingAmount)} UZS
					</div>
				</Card>
				<Card className="crm-card">
					<div className="text-gray-400 text-xs">Oylik standart reja</div>
					<div className="text-xl font-extrabold text-purple-600 mt-1">
						{formatMoney(sale.monthlyPayment)} UZS
					</div>
				</Card>
			</div>

			{/* Full Table */}
			<div className="crm-card crm-table-card">
				<Table
					dataSource={sale.schedule}
					columns={scheduleColumns}
					rowKey="index"
					pagination={false}
					scroll={{ x: 750 }}
					size="middle"
				/>
			</div>

			{/* Single Payment Modal */}
			<Modal
				title={`Muddati bo'yicha to'lov kiritish`}
				open={payModalVisible}
				onOk={handleSinglePaymentSubmit}
				onCancel={() => setPayModalVisible(false)}
				okText="To'lovni saqlash"
				cancelText="Bekor qilish"
				centered
			>
				<div className="space-y-4 pt-2">
					<div>
						<div className="block text-xs font-semibold mb-1">Grafik qatori</div>
						<Select
							value={preselectedScheduleId}
							onChange={handlePaymentRowChange}
							options={sale.schedule
								.filter((item) => item.status !== "paid")
								.map((item) => ({
									label: `#${item.index} - ${item.date} - ${formatMoney(
										Math.max(0, item.plannedAmount - item.paidAmount)
									)} UZS`,
									value: item.id,
								}))}
							className="w-full"
						/>
					</div>

					<div className="grid grid-cols-2 gap-4">
						<div>
							<div className="block text-xs font-semibold mb-1">To'lov summasi (UZS)</div>
							<InputNumber
								value={paymentAmount}
								onChange={(val) => setPaymentAmount(val || 0)}
								className="w-full"
								min={1}
								formatter={(value) => `${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
								parser={(value) => Number((value ?? "").replace(/\s/g, ""))}
							/>
						</div>
						<div>
							<div className="block text-xs font-semibold mb-1">To'lov usuli</div>
							<Select
								value={paymentMethod}
								onChange={setPaymentMethod}
								options={[
									{ label: "Naqd pul", value: "naqd" },
									{ label: "Bank o'tkazmasi", value: "bank" },
									{ label: "Karta orqali", value: "karta" },
								]}
								className="w-full"
							/>
						</div>
					</div>

					<div className="grid grid-cols-2 gap-4">
						<div>
							<div className="block text-xs font-semibold mb-1">Kvitansiya / Chek raqami</div>
							<Input
								value={paymentReceipt}
								onChange={(e) => setPaymentReceipt(e.target.value)}
								placeholder="Masalan: CH-2026..."
							/>
						</div>
						<div>
							<div className="block text-xs font-semibold mb-1">Sana</div>
							<DatePicker
								value={dayjs(paymentDate)}
								onChange={(_, value) => {
									if (!Array.isArray(value) && value) {
										setPaymentDate(value);
									}
								}}
								className="w-full"
								format="YYYY-MM-DD"
							/>
						</div>
					</div>

					<div>
						<div className="block text-xs font-semibold mb-1">Izoh</div>
						<Input
							value={paymentNote}
							onChange={(e) => setPaymentNote(e.target.value)}
							placeholder="Qo'shimcha tafsilotlar..."
						/>
					</div>
				</div>
			</Modal>

			{/* Early Payment Discount Modal */}
			<Modal
				title="Erta to'lov chegirma kalkulyatori"
				open={earlyPayModalVisible}
				onOk={handleEarlyPaymentSubmit}
				onCancel={() => setEarlyPayModalVisible(false)}
				okText="To'lovni tasdiqlash"
				cancelText="Yopish"
				width={600}
				centered
			>
				<div className="space-y-4 pt-2">
					<div className="crm-early-info">
						<InfoCircleOutlined />
						<span>
							Erta to'lov shkalasi bo'yicha chegirma tizimi:
							<br />- 3-5 oy oldindan to'lansa: <b>1%</b> chegirma
							<br />- 6-11 oy oldindan to'lansa: <b>3%</b> chegirma
							<br />- 12+ oy oldindan to'lansa: <b>5%</b> chegirma
						</span>
					</div>

					<div className="grid grid-cols-2 gap-4">
						<div>
							<div className="block text-xs font-semibold mb-1">Necha oy oldindan to'lanadi?</div>
							<Select
								value={earlyPayMonths}
								onChange={setEarlyPayMonths}
								options={[
									{ label: "3 oy", value: 3 },
									{ label: "6 oy", value: 6 },
									{ label: "12 oy", value: 12 },
								]}
								className="w-full"
							/>
						</div>
						<div>
							<div className="block text-xs font-semibold mb-1">To'lov usuli</div>
							<Select
								value={earlyPayMethod}
								onChange={setEarlyPayMethod}
								options={[
									{ label: "Bank o'tkazmasi", value: "bank" },
									{ label: "Naqd pul", value: "naqd" },
									{ label: "Karta orqali", value: "karta" },
								]}
								className="w-full"
							/>
						</div>
					</div>

					<div>
						<div className="block text-xs font-semibold mb-1">Kvitansiya / Chek raqami</div>
						<Input value={earlyPayReceipt} onChange={(e) => setEarlyPayReceipt(e.target.value)} />
					</div>

					<div className="crm-early-summary">
						<div className="crm-early-summary-row">
							<span>
								Jami oylar summasi ({discountInfo.rowsCount} oy x {formatMoney(sale.monthlyPayment)}
								):
							</span>
							<b>{formatMoney(discountInfo.rawSum)} UZS</b>
						</div>
						<div className="crm-early-summary-row">
							<span>Erta to'lov skidka foizi:</span>
							<b>{discountInfo.discountPercent}%</b>
						</div>
						<div className="crm-early-summary-row">
							<span>Skidka summasi:</span>
							<b>-{formatMoney(discountInfo.discountAmount)} UZS</b>
						</div>
						<div className="crm-early-summary-total">
							<span>To'lanadigan yakuniy summa:</span>
							<strong>{formatMoney(discountInfo.finalPayAmount)} UZS</strong>
						</div>
					</div>
				</div>
			</Modal>
		</div>
	);
}

export default PaymentSchedulePage;
